import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';
import {
  CellWithColSpan,
  ForceSelectionCallback,
  IndexedStrings,
  isCellWithColSpan,
  SelectableTableColumn,
  SelectableTablePossibleCellsType,
  TableSelection,
  UpHeaderColumn,
} from './SelectableTable.types';
import Pagination from '../Pagination.component';
import { IPageInfo } from '../../models/page.types';
import { useStyles } from './SelectableTable.styles';
import FiltersState from '../FiltersState/FiltersState.component';
import FilterStateGroup, { LabelWithValue } from '../../models/filterStateGroup.types';
import { NoRecords } from './../NoRecords/NoRecords.component';
import { SortableColumn } from '../SortableColumn/SortableColumn.component';
import { getOnInfiniteScroll } from '../../utils/infiniteScrolling';
import SelectAllCheckbox from './SelectAllCheckbox/SelectAllCheckbox.component';
import useSelectAll from '../../hooks/useSelectAll';
import { useHistory } from 'react-router-dom';
import { useTabNavigation } from '../../Context/TabNavigation.context';
import { ClickAwayListener, TableCell } from '@material-ui/core';
import FilterCell from './filters/FilterCell.component';
import { highContrastMode } from '../../utils/highContrastChecker';
import { AdvancedFilterState } from '../../pages/Assessment/Modals/AdvancedFilter/AdvancedFilterState.component';

interface SelectableTableProps<T, SortByType, FilterStateType = string> {
  upHeaderColumns?: UpHeaderColumn[];
  columns: SelectableTableColumn[];
  rows: T[];
  pageInfo?: IPageInfo;
  getPage?: (_page?: number, _newPageSize?: number) => void;
  filter?: IndexedStrings;
  filterElements?: { [key: string]: JSX.Element };
  onFilterChange?: (name: string, value: string) => void;
  totalCount?: number;
  onSelectionChanged?: (newSelection: TableSelection) => void;
  onItemSelectionChanged?: (itemId: string, isMultiChoice?: boolean) => void;
  forceSelectionRef?: (forceSelectionCallback: ForceSelectionCallback) => void;
  isMultiChoice?: boolean;
  children?: JSX.Element | ReactNode;
  withoutEmptyMessage?: boolean;
  withoutHeader?: boolean;
  withHeaderHidden?: boolean;
  noOverflowInitial?: boolean;
  oddTableDoesNotChangeColor?: boolean;
  grayBodyCell?: boolean;
  filterSearch?: boolean;
  headerDisabled?: boolean;
  filterbar?: FilterStateGroup<FilterStateType>[];
  isAdvancedFilterState?: boolean;
  onAdvancedFilterModalOpen?: () => void;
  onAdvancedFilterReset?: () => void;
  closeFilterItem?: (item?: string, propertyName?: keyof FilterStateType, dataItem?: LabelWithValue) => void;
  uncheckAllFilters?: () => void;
  onScrollTable?: (endReached: boolean) => void;
  sortByProperty?: string;
  isDescending?: boolean;
  setIsDescending?: (value: boolean) => void;
  setSortByColumn?: (value: SortByType) => void;
  withHighFilterRow?: boolean;
  hideCheckboxMenu?: boolean;
  isSelectionDisabled?: boolean;
  withoutFilterRow?: boolean;
  rowBackgroundColor?: string;
  activeRowBackgroundColor?: string;
  activeRowId?: string;
  cellsBorderBottom?: string;
  withColumnWidths?: boolean;
  maxHeight?: string;
  onRowClick?: (row: T) => void;
  onEnterMethod?: (id: string) => void;
  caption?: string;
  description?: string;
  simpleNavigatingDisabled?: boolean;
  selectedItemId?: string;
  setForceDeselectAllItems?: (value: boolean) => void;
  forceDeselectAllItems?: boolean;
  footer?: ReactNode;
}

const SelectableTable = <
  T extends {
    [key: string]: SelectableTablePossibleCellsType;
    externalRow?: ReactNode;
    stickyRowHeight?: string;
    rowBackground?: string;
  },
  SortByType = unknown,
  FilterStateType = string,
>({
  upHeaderColumns,
  columns,
  rows,
  pageInfo,
  getPage,
  filter,
  onFilterChange,
  filterElements,
  isMultiChoice = false,
  children,
  withoutEmptyMessage = false,
  withoutHeader = false,
  withHeaderHidden = false,
  noOverflowInitial = false,
  oddTableDoesNotChangeColor = false,
  grayBodyCell,
  filterSearch,
  headerDisabled,
  filterbar,
  isAdvancedFilterState,
  onAdvancedFilterModalOpen,
  onAdvancedFilterReset,
  closeFilterItem,
  uncheckAllFilters,
  onScrollTable,
  sortByProperty,
  isDescending,
  setIsDescending,
  setSortByColumn,
  onSelectionChanged,
  onItemSelectionChanged,
  forceSelectionRef,
  totalCount,
  withHighFilterRow,
  hideCheckboxMenu,
  isSelectionDisabled,
  withoutFilterRow,
  rowBackgroundColor,
  activeRowBackgroundColor,
  activeRowId,
  cellsBorderBottom,
  withColumnWidths,
  maxHeight,
  onRowClick,
  onEnterMethod,
  caption,
  description,
  simpleNavigatingDisabled,
  selectedItemId,
  setForceDeselectAllItems,
  forceDeselectAllItems,
  footer,
}: SelectableTableProps<T, SortByType, FilterStateType>): JSX.Element => {
  const styles = useStyles();
  const history = useHistory();

  const listInnerRef = useRef<HTMLDivElement | null>(null);

  const [isSimpleNavigating, setIsSimpleNavigating] = useState<boolean>(!simpleNavigatingDisabled);
  const [activeRowForSimpleNavigation, setActiveRowForSimpleNavigation] = useState<number | null>(null);

  const [activeRow, setActiveRow] = useState<number | null>(null);
  const [activeCol, setActiveCol] = useState<number | null>(null);

  const onStartNavigation = useCallback(() => {
    setIsSimpleNavigating(false);
    setActiveRow(0);
    setActiveCol(0);
  }, []);

  const onEndNavigation = useCallback((forceEnd) => {
    setActiveRow(null);
    setActiveCol(null);
    if (!forceEnd) {
      listInnerRef.current?.focus();
    }
  }, []);

  const {
    tabIndexForChildren: tabIndexForCells,
    isDuringNavigating: isNavigating,
    isDuringAction,
    TabNavigationArea,
    setActiveRowIndex,
    endNavigation,
    startAction,
  } = useTabNavigation(listInnerRef.current, onStartNavigation, onEndNavigation);

  const { selectedItemsIds, isAllItemsChecked, onItemSelectionToggle, onSelectAllItems, onDeselectAllItems } =
    useSelectAll();

  useEffect(() => {
    onSelectionChanged && onSelectionChanged({ items: selectedItemsIds, isAll: isAllItemsChecked });
  }, [selectedItemsIds, isAllItemsChecked, onSelectionChanged]);

  useEffect(() => {
    onDeselectAllItems && onDeselectAllItems();
    setForceDeselectAllItems && setForceDeselectAllItems(false);
  }, [forceDeselectAllItems]);

  const forceSelectionCallback = (selection: TableSelection) => {
    onSelectAllItems && onSelectAllItems(selection.items, !!selection.isAll);
  };

  useEffect(() => {
    onDeselectAllItems && onDeselectAllItems();
    setForceDeselectAllItems && setForceDeselectAllItems(false);
  }, [forceDeselectAllItems]);

  useEffect(() => {
    if (activeRow !== null) {
      setActiveRowIndex(activeRow);
    }
  }, [activeRow]);

  useEffect(() => {
    forceSelectionRef && forceSelectionRef(forceSelectionCallback);
  }, [forceSelectionRef]);

  const handleSelectionChange = useCallback(
    (row: T) => {
      onItemSelectionToggle(row.id as string, isMultiChoice ? isMultiChoice : undefined);
      onItemSelectionChanged && onItemSelectionChanged(row.id as string);
    },
    [isMultiChoice, onItemSelectionChanged, onItemSelectionToggle],
  );

  const getCell = (
    type: SelectableTableColumn['type'],
    column: SelectableTableColumn,
    row: T,
    isSelected: boolean,
  ): JSX.Element => {
    const cellValue = isCellWithColSpan(row[column.id]) ? (row[column.id] as CellWithColSpan).data : row[column.id];

    switch (type) {
      case 'children':
        if (React.isValidElement(children)) {
          return React.cloneElement(children);
        } else {
          return <>{cellValue}</>;
        }
      case 'checkbox':
        return (
          <Checkbox
            disableRipple
            color={'primary'}
            classes={{
              root: styles.checkbox,
            }}
            checked={isSelected}
            size={'medium'}
            onChange={() => handleSelectionChange(row)}
            inputProps={{ 'aria-label': 'checkbox' }}
            disabled={isSelectionDisabled}
            tabIndex={tabIndexForCells}
          />
        );
      case 'radio':
        return (
          <Radio
            color={'primary'}
            classes={{
              root: styles.checkbox,
            }}
            checked={isSelected}
            size={'small'}
            // used onClick to let it be unselected
            onClick={() => handleSelectionChange(row)}
            tabIndex={tabIndexForCells}
          />
        );

      default:
        return <>{cellValue}</>;
    }
  };

  const handleUncheckAllFilters = () => {
    if (uncheckAllFilters) {
      uncheckAllFilters();
    }
  };

  const handleCloseFilterItem = (item?: string, propertyName?: keyof FilterStateType, dataItem?: LabelWithValue) => {
    if (item && closeFilterItem) closeFilterItem(item, propertyName, dataItem);
  };

  const headerStyle = !headerDisabled ? styles.headerCell : [styles.headerCell, styles.disabled].join(' ');

  const isChecked: boolean = isAllItemsChecked
    ? true
    : !!selectedItemsIds &&
      selectedItemsIds.length > 0 &&
      (rows.map((row) => row.id) as string[]).every((e) => selectedItemsIds.includes(e));
  const totalItems: number = totalCount !== undefined ? totalCount : rows.length;

  const isIndeterminate: boolean = isAllItemsChecked
    ? false
    : !!selectedItemsIds &&
      selectedItemsIds.length > 0 &&
      selectedItemsIds.length < totalItems &&
      !isChecked &&
      (rows.map((row) => row.id) as string[]).some((e) => selectedItemsIds.includes(e));

  const numRows = rows.length;
  const numCols = columns.length;

  const hasAnyFilters = !withoutFilterRow && columns.some((column: SelectableTableColumn) => !!column.filterType);
  const hasFilterbar = filterbar && filterbar.length > 0;

  const extraRows: ('header' | 'filters' | 'filtersBar')[] = ['header'];

  if (hasAnyFilters) {
    extraRows.unshift('filters');
  }

  const headerRowIndex = -extraRows.indexOf('header') - 1;
  const filtersRowIndex = -extraRows.indexOf('filters') - 1;
  const minRowIndex = -extraRows.length;

  const scrollToRow = (row: number) => {
    if (row >= 1) {
      const rowId = 'table-row-' + row;
      document.querySelector('[data-testid=' + rowId + ']')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else {
      const element = document.getElementsByClassName('MuiTableRow-root')[0];
      if (element && element.scrollIntoView) {
        element.scrollIntoView();
      }
    }
  };

  const handleArrowUpForNavigationWCAG = () => {
    if (activeRow !== null && activeRow > minRowIndex) {
      setActiveRow(activeRow - 1);
    }
  };

  useEffect(() => {
    if (isNavigating && activeRow !== null && activeRow > 0) {
      scrollToRow(activeRow);
    }
  }, [activeRow, isNavigating]);

  const handleArrowDownForNavigationWCAG = () => {
    if (activeRow !== null && activeRow < numRows - 1) {
      setActiveRow(activeRow + 1);
    }
  };

  const handleArrowRightForNavigationWCAG = (isControlPressed: boolean) => {
    if (!isControlPressed) {
      if (activeCol !== null && activeCol < numCols - 1) {
        setActiveCol(activeCol + 1);
      }
    } else {
      setActiveCol(numCols - 1);
      setActiveRow(numRows);
      scrollToRow(numRows);
    }
  };

  const handleArrowLeftForNavigationWCAG = (isControlPressed: boolean) => {
    if (!isControlPressed) {
      if (activeCol !== null && activeCol > 0) {
        setActiveCol(activeCol - 1);
      }
    } else {
      setActiveCol(0);
      setActiveRow(0);
      scrollToRow(0);
    }
  };

  const handleSpaceForSimpleNavigation = useCallback(() => {
    if (isSimpleNavigating && activeRowForSimpleNavigation !== null && !isNavigating) {
      handleSelectionChange(rows[activeRowForSimpleNavigation]);
    }
  }, [activeRowForSimpleNavigation, handleSelectionChange, isNavigating, isSimpleNavigating, rows]);

  const handleEnterForSimpleNavigation = useCallback(() => {
    if (isSimpleNavigating && activeRowForSimpleNavigation !== null && !isNavigating) {
      const row = rows[activeRowForSimpleNavigation];
      const id = row.tableEnterId;
      if (onEnterMethod && id) {
        onEnterMethod(id.toString());
      } else {
        const tableEnterURL = row.tableEnterURL;
        if (tableEnterURL) history.push(tableEnterURL.toString());
      }
    }
  }, [isSimpleNavigating, activeRowForSimpleNavigation, isNavigating, rows, onEnterMethod, history]);

  const handleSpaceForNavigationWCAG = useCallback(() => {
    if (activeRow !== null && activeRow >= 0) {
      handleSelectionChange(rows[activeRow]);
    }
  }, [activeRow, rows]);

  const handleEnterForNavigationWCAG = useCallback(() => {
    if (activeRow !== null && activeCol !== null && activeRow >= 0) {
      const colIndex = activeCol;
      const rowIndex = activeRow;
      const row = rows[rowIndex];
      if (!columns[colIndex]?.skipEnterMethod) {
        const id = row.tableEnterId;
        if (onEnterMethod && id) {
          onEnterMethod(id.toString());
        } else {
          const tableEnterURL = row.tableEnterURL;
          if (tableEnterURL) history.push(tableEnterURL.toString());
        }
      }
    }
  }, [activeRow, activeCol, rows, columns]);

  const handleCtrRForActions = useCallback(() => {
    if (!isDuringAction) {
      setActiveRow(null);
      setActiveCol(null);
      startAction();
    } else endNavigation();
  }, []);

  useEffect(() => {
    if (isSimpleNavigating && activeRowForSimpleNavigation !== null) {
      scrollToRow(activeRowForSimpleNavigation);
      setIsSimpleNavigating(true);
    }
  }, [activeRowForSimpleNavigation, isSimpleNavigating]);

  useEffect(() => {
    if (isSimpleNavigating) {
      endNavigation(true);
    }
  }, [isSimpleNavigating]);

  const selectPrevRowForSimpleNavigation = useCallback(() => {
    setActiveRowForSimpleNavigation((prevState) => {
      if (prevState === null) {
        return 0;
      } else if (prevState > 0) {
        return prevState - 1;
      } else {
        return prevState;
      }
    });
    setIsSimpleNavigating(true);
  }, []);

  const selectNextRowForSimpleNavigation = useCallback(() => {
    setActiveRowForSimpleNavigation((prevState) => {
      if (prevState === null) {
        return 0;
      } else if (prevState < numRows - 1) {
        return prevState + 1;
      } else {
        return prevState;
      }
    });
    setIsSimpleNavigating(true);
  }, [numRows]);

  const handleGlobalKeyDown = useCallback(
    (e) => {
      const { key } = e;
      const isDialogOpened = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (!isDialogOpened && !simpleNavigatingDisabled) {
        switch (key) {
          case 'ArrowUp':
            if (e.metaKey || e.ctrlKey) {
              selectPrevRowForSimpleNavigation();
            }
            break;
          case 'ArrowDown':
            if (e.metaKey || e.ctrlKey) {
              selectNextRowForSimpleNavigation();
            }
            break;
          case ' ':
            if (isSimpleNavigating && document.activeElement?.tagName?.toLowerCase() !== 'input') {
              e.preventDefault();
              handleSpaceForSimpleNavigation();
            }
            break;
          case 'Enter':
            if (isSimpleNavigating) {
              handleEnterForSimpleNavigation();
            }
            break;
          default:
            break;
        }
      }
    },
    [
      simpleNavigatingDisabled,
      isSimpleNavigating,
      selectPrevRowForSimpleNavigation,
      selectNextRowForSimpleNavigation,
      handleSpaceForSimpleNavigation,
      handleEnterForSimpleNavigation,
    ],
  );

  const handleKeyDown = useCallback(
    (e) => {
      const { key } = e;
      const isDialogOpened = document.getElementsByClassName('MuiDialog-root').length > 0;
      if (!isDialogOpened) {
        if (isNavigating) {
          switch (key) {
            case 'ArrowUp':
              handleArrowUpForNavigationWCAG();
              e.preventDefault();
              break;
            case 'ArrowDown':
              handleArrowDownForNavigationWCAG();
              e.preventDefault();
              break;
            case 'ArrowRight':
              handleArrowRightForNavigationWCAG(e.metaKey || e.ctrlKey);
              e.preventDefault();
              break;
            case 'ArrowLeft':
              handleArrowLeftForNavigationWCAG(e.metaKey || e.ctrlKey);
              e.preventDefault();
              break;
            case ' ':
              e.preventDefault();
              handleSpaceForNavigationWCAG();
              break;
            case 'r':
              if (e.metaKey || e.ctrlKey) {
                handleCtrRForActions();
                e.preventDefault();
              }
              break;
            case 'Enter':
              handleEnterForNavigationWCAG();
              break;
            default:
              break;
          }
        }
      }
    },
    [activeRow, activeCol, isNavigating, numRows, numCols],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKeyDown);
    listInnerRef.current?.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      listInnerRef.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, handleGlobalKeyDown]);

  useEffect(() => {
    setIsSimpleNavigating(!simpleNavigatingDisabled);
  }, [simpleNavigatingDisabled]);

  const focusIfActive = (input: HTMLElement | null, colIndex: number | null, rowIndex: number) => {
    if (isDuringAction) return;
    input && (activeCol === colIndex || colIndex === null) && activeRow === rowIndex && input.focus();
    return input;
  };

  return (
    <>
      {pageInfo && <Pagination pageInfo={pageInfo} getPage={getPage} className={styles.pagination} />}
      <div className={styles.container}>
        <Paper elevation={0} className={!grayBodyCell ? styles.paper : styles.paperGray}>
          <ClickAwayListener
            onClickAway={() => {
              if (isNavigating) {
                endNavigation();
                setIsSimpleNavigating(false);
              }
            }}
          >
            <TableContainer
              className={`${styles.commonTableContainer} ${
                noOverflowInitial ? styles.tableContainerNoOverflowInitial : styles.tableContainer
              } ${highContrastMode ? styles.commonTableContainerPlaceholder : ''}`}
              onScroll={onScrollTable && getOnInfiniteScroll(onScrollTable, 10)}
              ref={listInnerRef}
              style={{ maxHeight: maxHeight, maxWidth: '100%' }}
              tabIndex={0}
            >
              <TabNavigationArea>
                <Table stickyHeader style={{ tableLayout: withColumnWidths ? 'fixed' : undefined }}>
                  {(caption || description) && (
                    <caption style={{ right: 0 }}>
                      {caption}

                      {description && <span> {description}</span>}
                    </caption>
                  )}

                  {!withoutHeader && (
                    <TableHead>
                      {upHeaderColumns && (
                        <TableRow>
                          {upHeaderColumns.map((column: UpHeaderColumn, colIndex: number) => {
                            return (
                              <TableCell
                                colSpan={column.colSpan}
                                key={column.id}
                                classes={{
                                  root: headerStyle,
                                }}
                                style={{
                                  textAlign: column.centered ? 'center' : undefined,
                                  top: '0px',
                                  position: 'sticky',
                                  borderBottom: 'solid 3px #FFFFFF',
                                  ...(column.style ? column.style : null),
                                }}
                                ref={(input: HTMLElement | null) => focusIfActive(input, colIndex, filtersRowIndex)}
                              >
                                {column.title}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )}
                      <TableRow>
                        {columns.map((column: SelectableTableColumn, colIndex: number) => (
                          <TableCell
                            tabIndex={tabIndexForCells}
                            onFocus={() => {
                              if (isNavigating) {
                                setActiveRow(headerRowIndex);
                                setActiveCol(colIndex);
                              }
                            }}
                            ref={(input: HTMLElement | null) => focusIfActive(input, colIndex, headerRowIndex)}
                            classes={{
                              root: headerStyle,
                            }}
                            style={{
                              textAlign: column.centered ? 'center' : undefined,
                              height: hideCheckboxMenu ? '42px' : withHeaderHidden ? '0px' : undefined,
                              paddingTop: withHeaderHidden ? '0px' : undefined,
                              paddingBottom: withHeaderHidden ? '0px' : undefined,
                              borderBottom: withHeaderHidden ? 'none' : undefined,
                              width: column.width,
                              top: !upHeaderColumns ? '0px' : '36px',
                              position: 'sticky',
                              ...(column.style ? column.style : null),
                            }}
                            key={`table-header-${column.id}-${colIndex}`}
                            role="columnheader"
                            aria-label="Zaznacz wszystkie elementy"
                          >
                            {!withHeaderHidden && (
                              <>
                                {(column.type === 'radio' && isMultiChoice) || column.type === 'checkbox' ? (
                                  <SelectAllCheckbox
                                    tabIndex={tabIndexForCells}
                                    isChecked={isChecked}
                                    isIndeterminate={isIndeterminate}
                                    disabled={isSelectionDisabled}
                                    hideCheckboxMenu={hideCheckboxMenu}
                                    onAllOnPageChange={() =>
                                      onSelectAllItems(
                                        rows.map((item) => item.id as string),
                                        false,
                                      )
                                    }
                                    onAllChange={() =>
                                      onSelectAllItems(
                                        rows.map((item) => item.id as string),
                                        true,
                                      )
                                    }
                                    onDeselectAllItems={onDeselectAllItems}
                                  />
                                ) : column.sortable &&
                                  isDescending !== undefined &&
                                  setIsDescending &&
                                  setSortByColumn &&
                                  sortByProperty ? (
                                  <SortableColumn<SortByType>
                                    tabIndex={tabIndexForCells}
                                    id={column.id as unknown as SortByType}
                                    columnName={column.title}
                                    isActiveSorting={sortByProperty === column.id}
                                    isDescending={isDescending}
                                    setIsDescending={setIsDescending}
                                    setSortByColumn={setSortByColumn}
                                  />
                                ) : (
                                  column.title
                                )}
                              </>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                  )}

                  <TableBody>
                    {hasAnyFilters && (
                      <TableRow>
                        {columns.map((column: SelectableTableColumn, colIndex: number) => (
                          <TableCell
                            tabIndex={tabIndexForCells}
                            onFocus={() => {
                              if (isNavigating) {
                                setActiveRow(filtersRowIndex);
                                setActiveCol(colIndex);
                              }
                            }}
                            ref={(input: HTMLElement | null) => focusIfActive(input, colIndex, filtersRowIndex)}
                            classes={{
                              root: headerStyle,
                            }}
                            key={`table-filter-${column.id}-${colIndex}`}
                            style={{
                              top: isMultiChoice && !noOverflowInitial ? 47 : !upHeaderColumns ? 36 : 72,
                              position: 'sticky',
                              ...(column.style ? column.style : null),
                            }} // enables the line of filters to be sticked below column header
                          >
                            <span style={{ cursor: 'pointer' }}>
                              <FilterCell
                                key={`{${column.id}_${caption}`}
                                type={column.filterType}
                                name={column.id}
                                filter={filter}
                                filterElements={filterElements}
                                tabIndex={tabIndexForCells}
                                onFilterChange={onFilterChange}
                              />
                            </span>
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                    {hasFilterbar && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          style={{
                            top: withHighFilterRow ? 86 : 74,
                            position: 'sticky',
                            zIndex: 1,
                          }}
                        >
                          <FiltersState<FilterStateType>
                            isOpen={filterbar.length > 0}
                            filterState={filterbar}
                            handleCloseFilterItem={handleCloseFilterItem}
                            handleUncheckAllFilters={handleUncheckAllFilters}
                            noSpan={true}
                            tabIndex={tabIndexForCells}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    {isAdvancedFilterState && (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          style={{
                            top: withHighFilterRow ? 86 : 74,
                            position: 'sticky',
                            zIndex: 1,
                            padding: '0px',
                          }}
                        >
                          <AdvancedFilterState
                            onModalOpen={onAdvancedFilterModalOpen}
                            onResetFilter={onAdvancedFilterReset}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    {rows.map((row: T, rowIndex: number) => {
                      let colsToSpan = 0;
                      return (
                        <TableRow
                          key={row['id'] ? `table-row-${row['id']}` : `table-row-${rowIndex}`}
                          role="row"
                          data-testid={`table-row-${rowIndex}`}
                          onClick={onRowClick ? () => onRowClick(row) : undefined}
                          hover={true}
                          style={{
                            backgroundColor:
                              isSimpleNavigating && activeRowForSimpleNavigation === rowIndex
                                ? 'rgba(148, 148, 203, 0.3)'
                                : activeRowId && activeRowBackgroundColor && activeRowId === row['id']
                                ? activeRowBackgroundColor
                                : row['rowBackground']
                                ? row['rowBackground']
                                : rowBackgroundColor,
                          }}
                        >
                          {!row.externalRow ? (
                            <>
                              {columns.map((column: SelectableTableColumn, colIndex: number) => {
                                const isSelected =
                                  isAllItemsChecked ||
                                  !!selectedItemsIds.find((item: string) => item === row['id']) ||
                                  (selectedItemId !== undefined &&
                                    row['id'] !== undefined &&
                                    selectedItemId === row['id']);

                                const isOdd = rowIndex % 2 === 0;
                                let bodyCellClassName = [
                                  styles.bodyCell,
                                  ...(isOdd && !oddTableDoesNotChangeColor ? [styles.bodyCellOdd] : []),
                                ].join(' ');
                                bodyCellClassName = grayBodyCell ? styles.grayBodyCell : bodyCellClassName;

                                const selectedCellStyle =
                                  isSelected && oddTableDoesNotChangeColor
                                    ? styles.bodyCellSelectionGray
                                    : styles.bodyCellSelected;

                                const bodyCellWithoutCheckboxesClassName =
                                  column.type !== 'radio' && column.type !== 'checkbox'
                                    ? styles.bodyCellwithoutCheckboxes
                                    : '';

                                const tableCellClasses = {
                                  root: isSelected
                                    ? [bodyCellClassName, bodyCellWithoutCheckboxesClassName, selectedCellStyle].join(
                                        ' ',
                                      )
                                    : [bodyCellClassName, bodyCellWithoutCheckboxesClassName].join(' '),
                                };

                                const colSpan = isCellWithColSpan(row[column.id])
                                  ? (row[column.id] as CellWithColSpan).colSpan
                                  : 0;

                                const showCell = colsToSpan === 0;
                                if (colSpan > 0) {
                                  colsToSpan += colSpan;
                                }
                                if (colsToSpan > 0) {
                                  colsToSpan--;
                                }

                                if (showCell) {
                                  return (
                                    <TableCell
                                      tabIndex={column.skipEnterMethod ? -1 : tabIndexForCells}
                                      onFocus={() => {
                                        if (isNavigating) {
                                          setActiveRow(rowIndex);
                                          setActiveCol(colIndex);
                                        }
                                      }}
                                      ref={(input: HTMLElement | null) => focusIfActive(input, colIndex, rowIndex)}
                                      classes={tableCellClasses}
                                      key={`table-filter-${column.id}-r-${rowIndex}`}
                                      style={{
                                        width: +colSpan > 0 ? undefined : column.width,
                                        borderBottom:
                                          rowIndex < rows.length - 1 && cellsBorderBottom
                                            ? cellsBorderBottom
                                            : undefined,
                                        position: row.stickyRowHeight ? 'sticky' : undefined,
                                        top: row.stickyRowHeight ? 0 : undefined,
                                        left: row.stickyRowHeight ? 0 : undefined,
                                        height: row.stickyRowHeight,
                                        background: row.stickyRowHeight ? rowBackgroundColor : undefined,
                                        ...(column.style ? column.style : null),
                                      }}
                                      colSpan={colSpan > 0 ? colSpan : undefined}
                                    >
                                      {getCell(column.type, column, row, isSelected)}
                                    </TableCell>
                                  );
                                } else {
                                  return undefined;
                                }
                              })}
                            </>
                          ) : (
                            <TableCell
                              colSpan={columns.length}
                              style={{
                                padding: '0',
                              }}
                            >
                              {row.externalRow}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {!rows.length && !withoutEmptyMessage && (
                      <TableRow>
                        <TableCell tabIndex={0} colSpan={columns.length}>
                          Brak wyników
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TabNavigationArea>
              {!rows.length && filterSearch && <NoRecords iconOnTop withMargin />}
              {footer}
            </TableContainer>
          </ClickAwayListener>
        </Paper>
      </div>
    </>
  );
};

const SelectableTableMemo = React.memo(SelectableTable) as typeof SelectableTable;

export { SelectableTableMemo as SelectableTable };
